import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import styles from './video-button.module.scss'
import cn from 'classnames'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import api from 'api/api'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import WithTooltip from 'components/shared/WithTooltip'
import { areSamePreviewUrl, getLastUrlPathSegment } from 'components/answers/payloadRemoval'
import useStore from 'state/knovStore'
import { useBsvExchangeRate, useBsvFeeRate } from 'api/useBsvExchangeRate'
import { SATOSHIS } from 'lib/bsv-util'
import perms from 'lib/perms'
import { useTemporalWorkflowStatus } from 'hooks/useTemporalWorkflowStatus'
import {
    showOrdinalMintStartedToast,
    showOrdinalMintSuccessToast,
    showOrdinalMintErrorToast,
} from 'components/shared/OrdinalMintStatusListener'
import { confirmAsync } from 'components/shared/confirmUtils'
import { OrdinalInfoPopup } from 'components/answers/Answer/AnswerImage/OrdinalInfoPopup'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import {
    Check,
    Disc3,
    Download,
    Film,
    Info,
    ListMusic,
    MoreVertical,
    Pencil,
    PictureInPicture2,
    Sparkles,
    Video as VideoIcon,
} from 'lucide-react'
import TvMinimalPlayIcon from 'components/shared/icons/TvMinimalPlayIcon'
import {
    openTrackInCinema,
    playTrackInDock,
    toggleTrackInPlaylist,
} from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import { dispatchMediaAiActionWhenThreadReady } from 'utils/mediaAiActionDispatcher'
import VideoPlayerCore from './VideoPlayerCore'
import {
    resetOptimisticVideoPreviewTime,
    resolveOptimisticVideoPreviewSource,
} from './videoPreviewSource'
import {
    mediaBucketsEnabled,
    resolveRecordingFrameHeight,
    payloadMediaContentWidth,
} from 'lib/mediaBuckets'

const MINTABLE_VIDEO_MAX_MB = 50
const MINTABLE_VIDEO_MAX_BYTES = MINTABLE_VIDEO_MAX_MB * 1024 * 1024
const MINT_TX_OVERHEAD_BYTES = 600
const MINT_FEE_SAFETY_MARGIN = 1.2
const Video = props => {
    const { videoTagRef } = props.refObj || {}
    const ordinalButtonRef = useRef(null)
    const videoActionMenuRef = useRef(null)
    const answerId = props.answer?.id
    const videoSourceKey = props.answer?.mp4_recording_url || props.answer?.recording_url || null
    const mediaKey = useMemo(
        () =>
            answerId && videoSourceKey
                ? `answer-video:${String(answerId)}:${videoSourceKey}`
                : null,
        [answerId, videoSourceKey],
    )
    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    const [isLoading, setIsLoading] = useState(false)
    const [isOrdinalInfoVisible, setIsOrdinalInfoVisible] = useState(false)
    const [resolvedVideoOrdinal, setResolvedVideoOrdinal] = useState(
        props.answer?.video_bsv_ordinal || null,
    )
    const [mintingWorkflowId, setMintingWorkflowId] = useState(null)
    const [mintingRunId, setMintingRunId] = useState(null)
    const [mintingAccessToken, setMintingAccessToken] = useState(null)
    const [isVideoActionMenuOpen, setIsVideoActionMenuOpen] = useState(false)

    const ordinalsEnabled = useUserHasFeature('mint_video')
    const openOrdinalsModal = useStore(state => state.actions.modalActions.openOrdinalsModal)
    const bsvExchangeRate = useBsvExchangeRate()
    const bsvFeeRateSatsPerByte = useBsvFeeRate()
    const currentUserId = window.gon?.currentUser?.id
    const isAnswerOwner = props.answer?.user_id === currentUserId
    // The file leaves the app by its poster's hand, or a knov_admin's.
    const canDownloadVideo = isAnswerOwner || perms.amKnovAdmin()
    const isAuthenticated = Boolean(currentUserId)
    const resolvedVideoUrl = props.answer?.mp4_recording_url || props.answer?.recording_url || null
    const shouldPrimeOptimisticPreview = Boolean(
        props.answer?.isLocal && !props.answer?.recording_thumbnail_url,
    )
    const previewVideoUrl = resolveOptimisticVideoPreviewSource(
        resolvedVideoUrl,
        shouldPrimeOptimisticPreview,
    )

    // Find matching BSV attachment - prefer mp4_recording if available
    const matchingBsvAttachment = useMemo(() => {
        const { answer } = props
        if (!answer?.bsv_attachments || !answer?.recording_info?.attachment_id) return null

        // Find attachment matching the recording_info.attachment_id
        return answer.bsv_attachments.find(
            att => att.attachment_id === answer.recording_info.attachment_id,
        )
    }, [props.answer?.bsv_attachments, props.answer?.recording_info?.attachment_id])

    const isRemoteMediaUrl = useCallback(url => {
        if (typeof url !== 'string') return false
        return !url.startsWith('blob:') && !url.startsWith('data:')
    }, [])

    const hasMintableVideo = useMemo(() => {
        if (!props.answer?.id) return false
        if (props.editMode) return false
        return (
            isRemoteMediaUrl(props.answer?.mp4_recording_url) ||
            isRemoteMediaUrl(props.answer?.recording_url)
        )
    }, [
        props.answer?.id,
        props.answer?.mp4_recording_url,
        props.answer?.recording_url,
        props.editMode,
        isRemoteMediaUrl,
    ])

    const videoByteSize = Number(
        props.answer?.mint_video_byte_size || matchingBsvAttachment?.size || 0,
    )
    const isVideoSizeKnown = videoByteSize > 0
    const isVideoTooLargeToMint = isVideoSizeKnown && videoByteSize > MINTABLE_VIDEO_MAX_BYTES

    const shouldTrackMintWorkflow = Boolean(
        isAuthenticated && isAnswerOwner && mintingWorkflowId && mintingRunId && mintingAccessToken,
    )

    const hasVideoOrdinal = Boolean(resolvedVideoOrdinal?.id || props.answer?.has_ordinal)
    const videoOrdinalRecord = Object(resolvedVideoOrdinal || props.answer?.video_bsv_ordinal || {})
    const videoOrdinalOrigin = videoOrdinalRecord.origin || ''
    const videoBlockchainTransactionId =
        videoOrdinalRecord?.mint_tx?.tx_id ||
        videoOrdinalRecord?.mint_tx_tx_id ||
        videoOrdinalRecord?.tx_id ||
        videoOrdinalOrigin.split(/[_:]/)[0] ||
        matchingBsvAttachment?.bsv_tx?.tx_id ||
        null
    const videoBlockchainExplorerUrl = bsvTxExplorerUrl(videoBlockchainTransactionId)
    const videoBlockchainActionIsOrdinal = hasVideoOrdinal || !matchingBsvAttachment
    const videoBlockchainActionLabel = videoBlockchainActionIsOrdinal ? 'Ordinal' : 'Blockchain'

    useEffect(() => {
        if (props.answer?.video_bsv_ordinal?.id) {
            setResolvedVideoOrdinal(props.answer.video_bsv_ordinal)
        }
    }, [props.answer?.video_bsv_ordinal])

    const {
        isPolling: isMintingInProgress,
        startPolling: startMintingPolling,
        error: mintingError,
    } = useTemporalWorkflowStatus(
        mintingWorkflowId,
        mintingRunId,
        'progress',
        'AppTemporal::Workflows::MintOrdinalWorkflow',
        {
            enabled: shouldTrackMintWorkflow,
            pollInterval: 2000,
            eventBusTopic: 'mint_ordinal_status',
            logPrefix: 'Minting video',
            accessToken: mintingAccessToken || undefined,
            onComplete: async () => {
                setIsLoading(false)
                setMintingWorkflowId(null)
                setMintingRunId(null)
                setMintingAccessToken(null)

                // Same toast OrdinalMintStatusListener shows; single slot dedupes the
                // double delivery when both EventBus and polling report completion.
                showOrdinalMintSuccessToast(props.answer?.id)
            },
            onError: error => {
                setIsLoading(false)
                setMintingWorkflowId(null)
                setMintingRunId(null)
                setMintingAccessToken(null)

                if (!isAnswerOwner) return

                showOrdinalMintErrorToast(props.answer?.id, error)
            },
        },
    )

    const isVideoMinting = Boolean(
        isLoading ||
            isMintingInProgress ||
            props.answer?.mint_workflow_id ||
            props.answer?.mint_run_id,
    )

    // Render BSV attachment status using shared utility
    const renderBsvAttachmentStatus = useCallback(
        (matchingBsvAttachment, { disableLink = false } = {}) => {
            const bsvTx = matchingBsvAttachment?.bsv_tx
            const txId = bsvTx?.tx_id
            return (
                <BsvTransactionStatusIcon
                    bsvTx={bsvTx}
                    fallbackTxId={txId}
                    className={styles.videoActionMenuStatusIcon}
                    wrapperProps={
                        disableLink
                            ? undefined
                            : {
                                  href: bsvTxExplorerUrl(txId),
                                  target: txId ? '_blank' : undefined,
                                  rel: txId ? 'noopener noreferrer' : undefined,
                                  onClick: ev => ev.stopPropagation(),
                              }
                    }
                />
            )
        },
        [],
    )

    const renderVideoOrdinalButton = useCallback(() => {
        if (!hasVideoOrdinal) return null

        return (
            <div
                className={styles.ordinalButtonOverlay}
                onClick={async ev => {
                    ev.stopPropagation()
                    if (
                        !resolvedVideoOrdinal?.id &&
                        props.answer?.has_ordinal &&
                        props.answer?.id
                    ) {
                        try {
                            const ordinals = await api.listOrdinals()
                            const match = ordinals?.ordinals?.find(
                                o => String(o?.answer_id || '') === String(props.answer?.id),
                            )
                            if (match?.id) {
                                setResolvedVideoOrdinal(match)
                            }
                        } catch (e) {
                            console.error('Failed to resolve video ordinal details:', e)
                        }
                    }
                    setIsOrdinalInfoVisible(!isOrdinalInfoVisible)
                }}
                ref={ordinalButtonRef}
            >
                <OrdinalsIcon size={20} />
                {isOrdinalInfoVisible && resolvedVideoOrdinal?.id && (
                    <OrdinalInfoPopup
                        ordinal={resolvedVideoOrdinal}
                        onClose={() => setIsOrdinalInfoVisible(false)}
                        openOrdinalsModal={openOrdinalsModal}
                        buttonRef={ordinalButtonRef}
                    />
                )}
            </div>
        )
    }, [
        hasVideoOrdinal,
        isOrdinalInfoVisible,
        resolvedVideoOrdinal,
        props.answer?.has_ordinal,
        props.answer?.id,
        openOrdinalsModal,
    ])

    const formatBytesToMb = useCallback(bytes => {
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    }, [])

    const handleMintOrdinal = useCallback(
        async ev => {
            ev.stopPropagation()
            if (!hasMintableVideo) return

            let effectiveVideoByteSize = videoByteSize

            if (effectiveVideoByteSize <= 0 && props.answer?.id) {
                try {
                    const mediaInfo = await api.getMintMediaInfo(props.answer.id, 'video')
                    effectiveVideoByteSize = Number(mediaInfo?.byte_size || 0)
                } catch (error) {
                    console.error('Failed to resolve video byte size for mint estimate:', error)
                }
            }

            if (effectiveVideoByteSize <= 0) {
                await confirmAsync('Unable to determine video size for mint estimate.', {
                    confirmLabel: 'OK',
                    hideCancel: true,
                    iconType: 'error',
                })
                return
            }

            if (isVideoTooLargeToMint || effectiveVideoByteSize > MINTABLE_VIDEO_MAX_BYTES) {
                await confirmAsync(
                    `Video is too large to mint (${formatBytesToMb(
                        effectiveVideoByteSize,
                    )}). Max supported size is ${MINTABLE_VIDEO_MAX_MB}MB.`,
                    {
                        confirmLabel: 'OK',
                        hideCancel: true,
                        iconType: 'error',
                    },
                )
                return
            }

            if (!bsvExchangeRate || bsvExchangeRate <= 0) {
                await confirmAsync(
                    'Unable to fetch BSV exchange rate. Please try again in a moment.',
                    {
                        confirmLabel: 'OK',
                        hideCancel: true,
                        iconType: 'error',
                    },
                )
                return
            }

            // The server signs the fee quote shown here, so the mint start charges
            // this exact amount even if another server process has since repriced.
            const mintFeeQuote = await api.getOrdinalMintFeeQuote().catch(() => null)
            const mintPriceSats = Number(mintFeeQuote?.service_fee_satoshis)
            if (
                !Number.isSafeInteger(mintPriceSats) ||
                mintPriceSats <= 0 ||
                !mintFeeQuote?.service_fee_quote
            ) {
                await confirmAsync(
                    'Unable to quote the Treechat mint fee. Please try again in a moment.',
                    {
                        confirmLabel: 'OK',
                        hideCancel: true,
                        iconType: 'error',
                    },
                )
                return
            }
            const mintPriceUsd = (mintPriceSats / SATOSHIS) * bsvExchangeRate
            const estimatedOnchainWriteSats = Math.ceil(
                (effectiveVideoByteSize + MINT_TX_OVERHEAD_BYTES) *
                    bsvFeeRateSatsPerByte *
                    MINT_FEE_SAFETY_MARGIN,
            )
            const estimatedOnchainWriteUsd =
                (estimatedOnchainWriteSats / SATOSHIS) * bsvExchangeRate
            const estimatedTotalUsd = mintPriceUsd + estimatedOnchainWriteUsd

            window.logEv('BSV_MINT_START', {
                answer_id: props.answer?.id,
                quest_id: props.answer?.quest_id,
                media_type: 'video',
                mint_price_sats: mintPriceSats,
                mint_price_usd: mintPriceUsd,
                video_bytes: effectiveVideoByteSize || undefined,
                fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
                estimated_onchain_write_sats: estimatedOnchainWriteSats,
            })

            const confirmMintRes = await new Promise(resolve => {
                const { openConfirmTray } = useStore.getState().actions.trayActions
                const onCancel = () => resolve(false)
                const onConfirm = () => resolve(true)

                openConfirmTray(
                    React.createElement(
                        'div',
                        { style: { color: 'var(--color-text-primary)' } },
                        React.createElement(
                            'div',
                            {
                                style: {
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    opacity: 0.7,
                                    marginBottom: '6px',
                                },
                            },
                            'Mint Ordinal',
                        ),
                        React.createElement(
                            'div',
                            { style: { opacity: 0.85, marginTop: '6px' } },
                            `Includes $${mintPriceUsd.toFixed(
                                2,
                            )} platform fee and $${estimatedOnchainWriteUsd.toFixed(
                                2,
                            )} onchain write.`,
                        ),
                    ),
                    `MINT $${estimatedTotalUsd.toFixed(2)}`,
                    onConfirm,
                    {
                        onCancel,
                        hideCancel: true,
                        confirmVariant: 'bsv',
                        primaryActionFirst: true,
                    },
                )
            })

            if (!confirmMintRes) return

            setIsLoading(true)

            try {
                const mintResult = await api.mintOrdinalTemporal(props.answer?.id, {
                    treechat_fee_amount_sats: mintPriceSats,
                    service_fee_quote: mintFeeQuote.service_fee_quote,
                    media_type: 'video',
                })
                const {
                    workflow_id: workflowId,
                    run_id: runId,
                    access_token: accessToken,
                } = mintResult

                if (workflowId && runId && accessToken) {
                    setMintingWorkflowId(workflowId)
                    setMintingRunId(runId)
                    setMintingAccessToken(accessToken)
                    showOrdinalMintStartedToast(props.answer?.id)
                } else if (workflowId && runId) {
                    console.warn(
                        'Mint workflow started but no access_token returned - status polling disabled',
                    )
                    setIsLoading(false)
                } else {
                    throw new Error('Failed to start mint ordinal workflow')
                }
            } catch (error) {
                console.error('Failed to mint video ordinal:', error)
                setIsLoading(false)
                await confirmAsync(`Failed to mint ordinal: ${error}`, {
                    confirmLabel: 'OK',
                    hideCancel: true,
                    iconType: 'error',
                })
            }
        },
        [
            hasMintableVideo,
            isVideoTooLargeToMint,
            formatBytesToMb,
            videoByteSize,
            bsvExchangeRate,
            props.answer?.id,
            props.answer?.quest_id,
        ],
    )

    const renderMintButton = useCallback(() => {
        if (!ordinalsEnabled || !isAnswerOwner || !hasMintableVideo || hasVideoOrdinal) {
            return null
        }

        const isDisabled = isVideoMinting || isVideoTooLargeToMint
        let tip = 'Mint video as NFT'
        if (isVideoMinting) {
            tip = 'Minting in progress...'
        } else if (isVideoTooLargeToMint) {
            tip = `Video too large to mint (max ${MINTABLE_VIDEO_MAX_MB}MB)`
        }

        return (
            <div className={styles.mintButtonOverlay}>
                <WithTooltip tip={tip}>
                    <div
                        className={cn(styles.mintButton, {
                            [styles.loading]: isVideoMinting,
                            [styles.disabled]: isVideoTooLargeToMint,
                        })}
                        onClick={isDisabled ? undefined : handleMintOrdinal}
                        style={{
                            pointerEvents: isDisabled ? 'none' : 'auto',
                            opacity: isDisabled ? 0.7 : 1,
                        }}
                    >
                        {isVideoMinting ? (
                            <>
                                <span className={styles.buttonIcon}>
                                    <i className="fa fa-spinner fa-spin" />
                                </span>
                                MINTING...
                            </>
                        ) : (
                            <>
                                <span className={styles.buttonIcon}>
                                    <OrdinalsIcon size={16} />
                                </span>
                                MINT
                            </>
                        )}
                    </div>
                </WithTooltip>
            </div>
        )
    }, [
        ordinalsEnabled,
        isAnswerOwner,
        hasMintableVideo,
        hasVideoOrdinal,
        isVideoMinting,
        isVideoTooLargeToMint,
        handleMintOrdinal,
    ])

    useEffect(() => {
        if (shouldTrackMintWorkflow && !isMintingInProgress) {
            startMintingPolling()
        }
    }, [shouldTrackMintWorkflow, startMintingPolling, isMintingInProgress])

    useEffect(() => {
        if (
            props.answer?.has_ordinal &&
            (mintingWorkflowId || mintingRunId) &&
            !isMintingInProgress
        ) {
            setMintingWorkflowId(null)
            setMintingRunId(null)
            setMintingAccessToken(null)
        }
    }, [props.answer?.has_ordinal, mintingWorkflowId, mintingRunId, isMintingInProgress])

    useEffect(() => {
        if (mintingError && (mintingWorkflowId || mintingRunId)) {
            setMintingWorkflowId(null)
            setMintingRunId(null)
            setMintingAccessToken(null)
        }
    }, [mintingError, mintingWorkflowId, mintingRunId])

    useEffect(() => {
        if (!hasVideoOrdinal && isOrdinalInfoVisible) {
            setIsOrdinalInfoVisible(false)
        }
    }, [hasVideoOrdinal, isOrdinalInfoVisible])

    let arr, seoKey
    if (props.answer.path) {
        arr = props.answer.path.split('/')
        seoKey = arr[arr.length - 1]
    }

    useEffect(() => {
        if (!isVideoActionMenuOpen) return undefined
        const handlePointerDown = event => {
            if (videoActionMenuRef.current?.contains(event.target)) return
            setIsVideoActionMenuOpen(false)
        }
        document.addEventListener('pointerdown', handlePointerDown, true)
        return () => document.removeEventListener('pointerdown', handlePointerDown, true)
    }, [isVideoActionMenuOpen])

    const sourceAiRun = useMemo(() => {
        if (!resolvedVideoUrl) return null
        const runs = Array.isArray(props.answer?.ai_runs) ? props.answer.ai_runs : []
        const resolvedVideoSegment = getLastUrlPathSegment(resolvedVideoUrl)
        return (
            runs.find(run =>
                (Array.isArray(run?.output_urls) ? run.output_urls : []).some(output => {
                    const outputRecord = Object(output)
                    const outputUrls = Object(outputRecord.urls)
                    const outputUrl =
                        outputRecord.url ||
                        outputUrls.original ||
                        outputRecord.preview_url ||
                        outputRecord.previewUrl
                    const outputSegment = getLastUrlPathSegment(outputUrl)
                    return (
                        outputUrl &&
                        (areSamePreviewUrl(outputUrl, resolvedVideoUrl) ||
                            (resolvedVideoSegment && outputSegment === resolvedVideoSegment))
                    )
                }),
            ) || null
        )
    }, [props.answer?.ai_runs, resolvedVideoUrl])

    const insertVideoAiAction = useCallback(
        tag => {
            if (!resolvedVideoUrl) return
            const width = props.answer?.video_width
            const height = props.answer?.video_height
            dispatchMediaAiActionWhenThreadReady({
                detail: {
                    tag,
                    questId: props.answer?.quest_id,
                    childQuestId: props.answer?.child_quests?.[0]?.id || props.quest?.id || null,
                    video: {
                        url: resolvedVideoUrl,
                        previewImageUrl: props.answer?.recording_thumbnail_url || null,
                        label: 'Video input',
                        answerId: props.answer?.id,
                        attachmentId:
                            props.answer?.mp4_recording_attachment_id ||
                            props.answer?.recording_attachment_id ||
                            null,
                        blobId:
                            props.answer?.mp4_recording_blob_id ||
                            props.answer?.recording_blob_id ||
                            null,
                        runId: sourceAiRun?.id || null,
                        rootRemixAiRunId:
                            sourceAiRun?.root_remix_ai_run_id ||
                            sourceAiRun?.remix_ai_run_id ||
                            sourceAiRun?.id ||
                            null,
                    },
                    aspectRatio: width && height ? `${width}:${height}` : null,
                },
                openRight: props.openRight,
                sourcePanel: props.sourcePanel,
            })
            setIsVideoActionMenuOpen(false)
        },
        [
            props.answer?.child_quests,
            props.answer?.id,
            props.answer?.mp4_recording_attachment_id,
            props.answer?.mp4_recording_blob_id,
            props.answer?.quest_id,
            props.answer?.recording_attachment_id,
            props.answer?.recording_blob_id,
            props.answer?.recording_thumbnail_url,
            props.answer?.video_height,
            props.answer?.video_width,
            props.openRight,
            props.quest?.id,
            props.sourcePanel,
            resolvedVideoUrl,
            sourceAiRun,
        ],
    )

    const sourceAiActionPayload = useMemo(() => {
        const actionPayload = sourceAiRun?.action_payload || sourceAiRun?.actionPayload
        if (!actionPayload || typeof actionPayload !== 'object') return null

        const actionTag =
            actionPayload.tag ||
            actionPayload.action_tag ||
            actionPayload.actionTag ||
            sourceAiRun?.action_tag ||
            sourceAiRun?.actionTag ||
            null
        return {
            ...actionPayload,
            remix_ai_run_id: sourceAiRun?.id || sourceAiRun?.ai_run_id || null,
            root_remix_ai_run_id:
                sourceAiRun?.root_remix_ai_run_id ||
                sourceAiRun?.rootRemixAiRunId ||
                sourceAiRun?.remix_ai_run_id ||
                sourceAiRun?.remixAiRunId ||
                sourceAiRun?.id ||
                sourceAiRun?.ai_run_id ||
                null,
            remix_kind: 'remix',
            remix_source: {
                ...(actionPayload.remix_source || actionPayload.remixSource || {}),
                ai_run_id: sourceAiRun?.id || sourceAiRun?.ai_run_id || null,
                root_remix_ai_run_id:
                    sourceAiRun?.root_remix_ai_run_id ||
                    sourceAiRun?.rootRemixAiRunId ||
                    sourceAiRun?.remix_ai_run_id ||
                    sourceAiRun?.remixAiRunId ||
                    sourceAiRun?.id ||
                    sourceAiRun?.ai_run_id ||
                    null,
                answer_id: props.answer?.id || null,
            },
            tag: actionTag || actionPayload.tag,
        }
    }, [props.answer?.id, sourceAiRun])

    const remixVideoAiAction = useCallback(() => {
        if (!sourceAiActionPayload) {
            useStore.getState().showGlobalNotice('No remix metadata was found for this video.', {
                variant: 'error',
                durationMs: 5000,
            })
            setIsVideoActionMenuOpen(false)
            return
        }

        dispatchMediaAiActionWhenThreadReady({
            detail: {
                questId: props.answer?.quest_id,
                childQuestId: props.answer?.child_quests?.[0]?.id || props.quest?.id || null,
                aiAction: sourceAiActionPayload,
                remix: true,
            },
            openRight: props.openRight,
            sourcePanel: props.sourcePanel,
        })
        setIsVideoActionMenuOpen(false)
    }, [
        props.answer?.child_quests,
        props.answer?.quest_id,
        props.openRight,
        props.quest?.id,
        props.sourcePanel,
        sourceAiActionPayload,
    ])

    const showPromptInfo = useCallback(() => {
        const actionPayload = Object(sourceAiActionPayload)
        const actionInputs = Object(actionPayload.inputs)
        const textInput = Object(actionInputs.text)
        const prompt = actionPayload.prompt || textInput.value || null
        useStore
            .getState()
            .showGlobalNotice(prompt || 'No prompt metadata was found for this media.', {
                variant: prompt ? 'info' : 'error',
                durationMs: 8000,
            })
        setIsVideoActionMenuOpen(false)
    }, [sourceAiActionPayload])

    const renderVideoActionMenu = originEl =>
        resolvedVideoUrl ? (
            <div className={styles.videoActionMenu} ref={videoActionMenuRef}>
                <WithTooltip tip="Video actions">
                    <button
                        type="button"
                        className={cn(
                            styles.videoActionMenuButton,
                            (hasVideoOrdinal || isVideoMinting) && styles.ordinalMediaActionButton,
                        )}
                        onClick={ev => {
                            ev.preventDefault()
                            ev.stopPropagation()
                            setIsVideoActionMenuOpen(open => !open)
                        }}
                        aria-label="Video actions"
                        aria-haspopup="menu"
                        aria-expanded={isVideoActionMenuOpen}
                    >
                        {hasVideoOrdinal || isVideoMinting ? (
                            <OrdinalsIcon size={16} />
                        ) : (
                            <MoreVertical size={16} strokeWidth={2.25} />
                        )}
                    </button>
                </WithTooltip>
                {isVideoActionMenuOpen && (
                    <div
                        className={styles.videoActionMenuList}
                        role="menu"
                        onClick={ev => ev.stopPropagation()}
                    >
                        {videoBlockchainExplorerUrl || matchingBsvAttachment ? (
                            videoBlockchainExplorerUrl ? (
                                <a
                                    className={styles.videoActionMenuItem}
                                    href={videoBlockchainExplorerUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    role="menuitem"
                                    onClick={ev => ev.stopPropagation()}
                                >
                                    {videoBlockchainActionIsOrdinal ? (
                                        <OrdinalsIcon size={14} />
                                    ) : (
                                        renderBsvAttachmentStatus(matchingBsvAttachment, {
                                            disableLink: true,
                                        })
                                    )}
                                    <span>{videoBlockchainActionLabel}</span>
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.videoActionMenuItem}
                                    role="menuitem"
                                    onClick={() => setIsVideoActionMenuOpen(false)}
                                >
                                    {renderBsvAttachmentStatus(matchingBsvAttachment, {
                                        disableLink: true,
                                    })}
                                    <span>Blockchain</span>
                                </button>
                            )
                        ) : null}
                        {mediaKey && mediaDockCinemaEnabled() && (
                            <button
                                type="button"
                                className={styles.videoActionMenuItem}
                                role="menuitem"
                                onClick={event => {
                                    openTrackInCinema(mediaKey, 'media_menu', {
                                        originEl:
                                            event.currentTarget.closest(
                                                '[data-media-track-origin]',
                                            ) || originEl,
                                        returnFocusEl: videoActionMenuRef.current?.querySelector(
                                            'button[aria-haspopup="menu"]',
                                        ),
                                    })
                                    setIsVideoActionMenuOpen(false)
                                }}
                            >
                                <TvMinimalPlayIcon size={14} strokeWidth={2.2} />
                                <span>Cinema</span>
                            </button>
                        )}
                        {canDownloadVideo && (
                            <a
                                className={styles.videoActionMenuItem}
                                href={resolvedVideoUrl}
                                download={seoKey}
                                role="menuitem"
                                onClick={() => setIsVideoActionMenuOpen(false)}
                            >
                                <Download size={14} strokeWidth={2.2} />
                                <span>Download</span>
                            </a>
                        )}
                        {mediaKey && (
                            <button
                                type="button"
                                className={styles.videoActionMenuItem}
                                role="menuitem"
                                onClick={() => {
                                    toggleTrackInPlaylist(mediaKey, 'media_menu')
                                    setIsVideoActionMenuOpen(false)
                                }}
                            >
                                {inPlaylist ? (
                                    <Check size={14} strokeWidth={2.2} />
                                ) : (
                                    <ListMusic size={14} strokeWidth={2.2} />
                                )}
                                <span>{playlistMenuLabel(inPlaylist)}</span>
                            </button>
                        )}
                        {mediaKey && (
                            <button
                                type="button"
                                className={styles.videoActionMenuItem}
                                role="menuitem"
                                onClick={() => {
                                    playTrackInDock(mediaKey, 'media_menu')
                                    setIsVideoActionMenuOpen(false)
                                }}
                            >
                                <PictureInPicture2 size={14} strokeWidth={2.2} />
                                <span>{PLAY_IN_DOCK_LABEL}</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.videoActionMenuItem}
                            role="menuitem"
                            onClick={() => insertVideoAiAction('nb')}
                        >
                            <Pencil size={14} strokeWidth={2.2} />
                            <span>Edit</span>
                        </button>
                        <button
                            type="button"
                            className={styles.videoActionMenuItem}
                            role="menuitem"
                            onClick={() => insertVideoAiAction('animate')}
                        >
                            <Sparkles size={14} strokeWidth={2.2} />
                            <span>Animate</span>
                        </button>
                        <button
                            type="button"
                            className={styles.videoActionMenuItem}
                            role="menuitem"
                            onClick={() => insertVideoAiAction('gif')}
                        >
                            <Film size={14} strokeWidth={2.2} />
                            <span>GIF</span>
                        </button>
                        <button
                            type="button"
                            className={styles.videoActionMenuItem}
                            role="menuitem"
                            onClick={() => insertVideoAiAction('extend')}
                        >
                            <VideoIcon size={14} strokeWidth={2.2} />
                            <span>Extend</span>
                        </button>
                        <button
                            type="button"
                            className={styles.videoActionMenuItem}
                            role="menuitem"
                            onClick={showPromptInfo}
                        >
                            <Info size={14} strokeWidth={2.2} />
                            <span>Prompt</span>
                        </button>
                        {sourceAiActionPayload ? (
                            <button
                                type="button"
                                className={styles.videoActionMenuItem}
                                role="menuitem"
                                onClick={remixVideoAiAction}
                            >
                                <Disc3 size={14} strokeWidth={2.2} />
                                <span>Remix</span>
                            </button>
                        ) : null}
                    </div>
                )}
            </div>
        ) : null

    const renderPlayerOverlay = useCallback(
        ({ originEl }) => (
            <>
                {renderVideoOrdinalButton()}
                {renderMintButton()}
                {renderVideoActionMenu(originEl)}
            </>
        ),
        [renderMintButton, renderVideoActionMenu, renderVideoOrdinalButton],
    )

    const sourceType = props.answer?.mp4_recording_url
        ? 'video/mp4'
        : props.answer?.original_recording_type
    const handleLoadedMetadata = useCallback(
        event => {
            resetOptimisticVideoPreviewTime(event.currentTarget, shouldPrimeOptimisticPreview)
            props.onLoadedMetadata?.(event)
            props.onMediaLayoutSettled?.()
        },
        [props.onLoadedMetadata, props.onMediaLayoutSettled, shouldPrimeOptimisticPreview],
    )

    // Fixed frame: videos use the payload's aspect bucket while audio recordings use
    // a compact transport frame. The estimators call the same resolver; on mobile and inside
    // post-lens reply cards the frame is clamped to the shared payload content width so
    // contain-fit never letterboxes.
    const reservedVideoStyle = mediaBucketsEnabled()
        ? {
              height: resolveRecordingFrameHeight(
                  props.answer,
                  payloadMediaContentWidth({
                      postLens: !!props.postLens,
                      cardWidth: props.estimatedWidth,
                  }),
              ),
          }
        : undefined

    //console.log('render video', props)
    return (
        <VideoPlayerCore
            className="video-comp"
            mediaKey={mediaKey}
            src={previewVideoUrl}
            type={sourceType}
            trackTitle={
                props.answer?.user?.name || props.answer?.user_name
                    ? `Video · ${props.answer?.user?.name || props.answer?.user_name}`
                    : undefined
            }
            trackAnswerId={answerId}
            posterSrc={props.answer?.recording_thumbnail_url}
            width={props.answer?.video_width}
            height={props.answer?.video_height}
            videoRef={videoTagRef}
            containerClassName={styles.videoContainer}
            containerStyle={reservedVideoStyle}
            preloadVideoLink
            renderOverlay={renderPlayerOverlay}
            onLoadedMetadata={handleLoadedMetadata}
            onPosterLoad={props.onMediaLayoutSettled}
        />
    )
}

// no video
// edit

// video
// edit
// edit
// unprocessed
// processed

const prevKeys = {}

const areEqual = (prevProps, nextProps) => {
    if (prevProps.editMode !== nextProps.editMode) return false

    const nextAnswer = nextProps.answer
    const prevAnswer = prevProps.answer

    let equal = true

    // mark as not equal so we update the ui when the bsv attachment status changes
    if (
        JSON.stringify(nextAnswer?.bsv_attachments) !== JSON.stringify(prevAnswer?.bsv_attachments)
    ) {
        return false
    }

    if (nextAnswer?.has_ordinal !== prevAnswer?.has_ordinal) {
        return false
    }

    if (nextProps.editedVideoUrl) {
        // We're editing and ...
        if (
            nextAnswer.mp4_recording_url &&
            nextAnswer.mp4_recording_url !== prevAnswer.mp4_recording_url
        ) {
            // This overrides url link if an updated processed vid comes back.
            //console.log('edited mp4 show download')
            // Update via DOM so no need to set equal to false.
            nextProps.showDownload(nextAnswer)
        } else if (nextProps.editedVideoUrl !== prevProps.editedVideoUrl) {
            // This means we updated the edited video.
            //console.log('edited url')
            equal = false
            prevKeys[nextAnswer.id] = nextProps.editedVideoUrl
        }
    } else if (
        nextAnswer.mp4_recording_url &&
        nextAnswer.mp4_recording_url !== prevAnswer.mp4_recording_url
    ) {
        // Not editing, but updated url comes back, perhaps from a newly created video or a different machine?
        // TODO might want to add mp4_recording_key to cached answers for consistency, using url for now.
        //console.log('mp4 show download')
        // Update via DOM so no need to set equal to false.
        nextProps.showDownload(nextAnswer)
    } else if (nextAnswer.recording_key && nextAnswer.recording_key !== prevAnswer.recording_key) {
        equal = false
    } else if (nextAnswer.recording_thumbnail_url !== prevAnswer.recording_thumbnail_url) {
        // Re-render when thumbnail URL changes (becomes available or updates)
        equal = false
    }
    //console.log('equal', equal)
    return equal
}

export default React.memo(Video, areEqual)
